import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import Input from "../BaseControls/Input";
import { controlPropTypes } from "../formhelpers";
import ReactCalendar from "react-calendar/dist/entry.nostyle";
import DropdownWrapper from "../../components/DropdownWrapper";
import { processKeyName } from "./utils/keyEvents";

export const CALENDAR_KEY = "c";
export const NOW_KEY = "t";

export default class Calendar extends React.Component {
    constructor(props) {
        super(props);
        this.inputRef = React.createRef();
        this.calendarButton = React.createRef();

        this.onCalendarClick = this.onCalendarClick.bind(this);
        this.onCalendarMouseDown = this.onCalendarMouseDown.bind(this);
        this.onCalendarChange = this.onCalendarChange.bind(this);
        this.onCommitChange = this.onCommitChange.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onFocus = this.onFocus.bind(this);

        this.state = {
            showDatePicker: false,
            hasFocus: false,
        };
        this.clickedCalendar = false;

        // setup keyboard shortcuts
        this.keyboardShortcuts = [{
            key: NOW_KEY,
            name: "Set to now",
        }];

        if(props.showCalendar) {
            this.keyboardShortcuts.push({
                key: CALENDAR_KEY,
                name: "Show calendar",
            });
        }
    }

    convertStringToMoment(value) {
        const {maskFormat} = this.props;
        if(value && value !== maskFormat.placeholder) {
            if (value != null && typeof value === "string" && value.length !== 0) {
                // on commit check if the user entered a partial year...
                // find year chunk in the mask.
                const yearIndex = maskFormat.mask.indexOf("YYYY");
                if(yearIndex !== -1) {
                    const m = moment(value, maskFormat.momentjs);
                    let fullYear = value.substr(yearIndex,4);
                    const yearInt = parseInt(fullYear, 10) | 0;
                    let yearString = yearInt.toString();
                    if (yearString.length < 3) {
                        const today = new Date();
                        const digits = parseInt(today.getFullYear().toString().slice(2), 10);
                        //pad single
                        if (yearString.length === 1) {
                            yearString = "0" + yearString;
                        }
                        if (parseInt(yearInt, 10) <= digits) {
                            fullYear = "20" + yearString;
                        } else {
                            fullYear = "19" + yearString;
                        }
                        m.year(parseInt(fullYear, 10));
                        value = m.format(maskFormat.momentjs);
                    }
                }
            }
            // convert value string to moment
            value = moment(value, maskFormat.momentjs);
        }
        else {
            value = null;
        }
        return value;
    }

    convertMomentToString(value) {
        const {maskFormat} = this.props;
        if(value) {
            //convert Date to string
            const m = moment(value);
            return m.format(maskFormat.momentjs);
        }
        return null;
    }

    onCommitChange(descriptor, newValueStr) {
        const {onCommitChange, enabled, value} = this.props;
        /*
         * Take note that this control only returns moment() objects.
         */
        const newValue = this.convertStringToMoment(newValueStr);
        if(this.state.showDatePicker) {
            this.setState({showDatePicker: false});
        }
        if(newValueStr !== this.convertMomentToString(value)) {
            // TODO: Figure out why "Enter" key and blur fire two commit changes.
            // So far this issue does not seem to be causing any problems.
            // But it may be something to be aware of.

            // only commit change if the value actually changed.
            enabled && onCommitChange && onCommitChange(descriptor, newValue);
        }
        // convertStringToMoment fixes partial date strings.
        // this make sure the date input shows the actual string value being committed.
        // temporary until the next render comes around.
        this.inputRef.current.value = this.convertMomentToString(newValue);
    }

    onCalendarChange(dateValue) {
        const {descriptor, maskFormat} = this.props;
        this.onCommitChange(descriptor, moment(dateValue).format(maskFormat.momentjs));
    }

    onCalendarMouseDown() {
        // This is to get ahead of the input blur/focus events. Allows us to not
        // bubble up the focus or blur event when the calendar is being clicked.
        // Gets us around the alert on Illegal/Warning values loop.
        this.clickedCalendar = true;
    }

    onCalendarClick(e) {
        const {showCalendar, descriptor, enabled} = this.props;
        if(showCalendar && enabled && !this.state.showDatePicker) {
            // show calendar
            this.setState({showDatePicker: true});
            this.onFocus(e, descriptor, this.inputRef.current.value);
            // refocus the input control
            this.inputRef.current.focus();
        }
    }

    onFocus(e, descriptor, v) {
        const {enabled, onFocus, value} = this.props;
        if(this.state.hasFocus) {return;} // already focused
        this.setState({hasFocus: true});
        const newValue = this.convertStringToMoment(v);
        onFocus && onFocus(e, descriptor, enabled ? newValue : value, this.keyboardShortcuts);
    }

    onBlur(e, descriptor, v) {
        const {enabled, onBlur, value} = this.props;
        if(this.state.showDatePicker || this.clickedCalendar) {
            // date picker is closing or being opened
            // don't blur, instead refocus the input
            this.inputRef.current.focus();
            this.clickedCalendar = false;
            return;
        }
        this.clickedCalendar = false;
        this.setState({hasFocus: false});
        const newValue = this.convertStringToMoment(v);
        onBlur && onBlur(e, descriptor, enabled ? newValue : value);
    }

    onKeyDown(e, d, value) {
        const {descriptor, showCalendar, onKeyDown, enabled, maskFormat} = this.props;
        let isKeyboardShortcut = false;
        if(enabled) {
            const isModifier = ( e.ctrlKey || e.altKey || e.metaKey );
            const key = processKeyName(e.key);
            if (showCalendar && key === CALENDAR_KEY && !isModifier) {
                // CALENDAR_KEY: only show the date picker dropdown.
                this.onCalendarClick(e);
            }
            else if (key === NOW_KEY) {
                // NOW_KEY: commit and move focus
                isKeyboardShortcut = true;
                this.onCommitChange(descriptor, moment(new Date()).format(maskFormat.momentjs));
            }
            onKeyDown && onKeyDown(e, descriptor, this.convertStringToMoment(value), isKeyboardShortcut);
        }
    }

    getCurrentDate() {
        if(this.state.showDatePicker) {
            const dirtyValue = this.inputRef.current.value;
            if (dirtyValue && !isNaN(parseFloat(dirtyValue.charAt(0)))) {
                return moment(this.inputRef.current.value, this.props.maskFormat.momentjs).toDate();
            }
        }
        return new Date();
    }

    render() {
        const {maskFormat, showCalendar, controlOnly, icon, value} = this.props;
        const inputGroupStyle = controlOnly ? {} : {width: (maskFormat.mask.length + (showCalendar ? 3 : 0) + 2) * 0.8 + "em"};
        const {hasFocus} = this.state;
        return (
            <React.Fragment>
                <div className={(showCalendar || icon) ? "input-group" : ""} style={inputGroupStyle}>
                    <Input
                        _ref={this.inputRef}
                        {...this.props}
                        onCommitChange={this.onCommitChange}
                        onBlur={this.onBlur}
                        onFocus={this.onFocus}
                        onKeyDown={this.onKeyDown}
                        value={this.convertMomentToString(value)}
                        valueType="string"
                        maskFormat={maskFormat}
                        focused={hasFocus}
                    />
                    {(showCalendar || icon) && (
                        <span
                            ref={this.calendarButton}
                            onClick={this.onCalendarClick}
                            onMouseDown={this.onCalendarMouseDown}
                            className="calendar-button input-group-addon"
                        >
                            <span className={`glyphicon glyphicon-${icon ? icon : "calendar"} icon-calendar`} />
                        </span>
                    )}
                </div>
              {this.state.showDatePicker && <DropdownWrapper
                    targetRef={this.calendarButton}
                    autoSize={false}
                    align="left"
                    ignoreClickSelector=".calendar-container"
                    onHide={() => {
                        this.setState({showDatePicker: false});
                    }}
                >
                    <div className="calendar-container">
                        <ReactCalendar
                            onChange={this.onCalendarChange}
                            minDetail="decade"
                            calendarType="US"
                            showFixedNumberOfWeeks={true}
                            formatMonth={(locale, date) => moment(date).format("MMM")}
                            formatShortWeekday={(locale, date) => moment(date).format("dd")}
                            minDate={new Date("01/01/1850")}
                            maxDate={new Date("01/01/2100")}
                            value={this.getCurrentDate()}
                        />
                    </div>
                </DropdownWrapper>
              }
            </React.Fragment>
        );

    }
}

Calendar.propTypes = {
    ...controlPropTypes({
        granularity: PropTypes.string
    }),
    maskFormat: PropTypes.object.isRequired,
    showCalendar: PropTypes.bool.isRequired,
    controlOnly: PropTypes.bool,
    icon: PropTypes.string
};



// WEBPACK FOOTER //
// ./app/lib/common/descriptorcontrols/BaseControls/DateInput.js